// GM Forge admin panel (§8). Admin marks specific existing items as
// forgeable; the game's Workshop UI reads the same items grouped by
// rarity. No duplicated item definitions — the forge only references
// item IDs from gm_items. Persistence is server-side (gm_forge collection).

import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Check, Save } from "lucide-react";
import { api } from "@/lib/api";
import { gmStore, RARITIES } from "@/gm/gmStore";

export default function ForgeSection() {
  const [items, setItems] = useState(gmStore.items.list());
  const [selected, setSelected] = useState(new Set());
  const [initial, setInitial] = useState(new Set());
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.forgeList();
        const ids = new Set(r.itemIds || []);
        setSelected(ids);
        setInitial(new Set(ids));
      } catch (e) {
        // if the endpoint is not reachable, start empty
      }
    })();
  }, []);
  useEffect(() => {
    // Keep items in sync (admin might create new ones in the Items tab).
    const t = setInterval(() => setItems(gmStore.items.list()), 2000);
    return () => clearInterval(t);
  }, []);

  const toggle = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };
  const persist = async () => {
    setSaving(true);
    try {
      await api.forgeSet(Array.from(selected));
      setInitial(new Set(selected));
      toast.success("Forge salvata");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };
  const dirty = selected.size !== initial.size || Array.from(selected).some(x => !initial.has(x));

  const grouped = RARITIES.map(r => ({
    rarity: r,
    items: items.filter(i => i.rarity === r.key && (!q || (i.name || "").toLowerCase().includes(q.toLowerCase()))),
  }));

  return (
    <div className="space-y-3" data-testid="gm-forge-section">
      <div className="flex items-center gap-2">
        <div>
          <div className="font-heading text-sm tracking-[0.3em] uppercase text-[#E5C34A]">Forge</div>
          <div className="font-mono text-[9px] tracking-widest uppercase text-[#666]">
            {selected.size} oggetti forgiabili · raggruppati per rarità · persistito server-side
          </div>
        </div>
        <button data-testid="gm-forge-save" onClick={persist} disabled={!dirty || saving}
          className="ml-auto px-3 py-2 border border-[#6E8B3D] text-[#6E8B3D] font-heading text-[10px] tracking-widest uppercase hover:bg-[#4A5D23]/10 flex items-center gap-1.5 disabled:opacity-40">
          <Save className="w-3 h-3" /> {saving ? "Salvo…" : dirty ? "Salva modifiche" : "Salvato"}
        </button>
      </div>
      <input data-testid="gm-forge-search" value={q} onChange={e => setQ(e.target.value)}
        placeholder="Cerca oggetto..." className="w-full bg-[#0a0a0a] border border-[#333] px-2.5 py-2 text-xs text-[#E2DFD8] focus:border-[#B89C3A] outline-none" />
      {grouped.map(({ rarity, items: group }) => (
        <div key={rarity.key} className="panel-inner p-3 border border-[#333]" style={{ borderLeft: `3px solid ${rarity.color}` }}>
          <div className="font-heading text-[11px] tracking-widest uppercase mb-2" style={{ color: rarity.color }}>
            {rarity.key} <span className="text-[#666] font-mono text-[9px]">({group.length})</span>
          </div>
          {group.length === 0 ? (
            <div className="text-[10px] italic text-[#666]">Nessun oggetto di questa rarità.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {group.map(it => (
                <button key={it.id} type="button"
                  data-testid={`gm-forge-item-${it.id}`}
                  onClick={() => toggle(it.id)}
                  className={`text-left border px-3 py-2 font-mono text-[10px] transition-colors ${
                    selected.has(it.id)
                      ? "border-[#6E8B3D] text-[#E2DFD8] bg-[#4A5D23]/10"
                      : "border-[#262626] text-[#8c8c8c] hover:text-[#B0ADA5]"
                  }`}>
                  <div className="flex items-center gap-2">
                    {selected.has(it.id) ? <Check className="w-3 h-3 text-[#6E8B3D]" /> : <Sparkles className="w-3 h-3 text-[#666]" />}
                    <span className="truncate">{it.name}</span>
                  </div>
                  <div className="text-[#666] text-[9px] mt-0.5 truncate">{it.type}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
      <div className="text-[10px] font-mono text-[#666]">
        Suggerimento: gli oggetti selezionati compaiono nella sezione Workshop del gioco, raggruppati per rarità.
      </div>
    </div>
  );
}
